// A4.2 "New issue" drawer — opened ONLY by the "+" button (code list #7):
//   from A4 the component is pre-filled; from A3 it is left to pick.
// The code shown (e.g. LZ-49) is a PREVIEW; the database assigns the real number when saving.
// v0.6.0 Part 6: Snapshots — drag pictures onto the box, paste, or click to pick; uploaded right after the issue is saved.
// The gridline pin is placed on the issue page (A5.2) once the component has a plan.
import { useRef, useState } from "react";
import { useLeaveGuard } from "../../../core/leaveGuard";
import { rpc } from "../../../core/api/backend";
import { ConfirmDiscard, DateInput, Drawer, ErrorBox, Field, InfoIcon, toast } from "../../../core/ui/kit";
import { DATE_HINT_ROW } from "../../../core/format";
import type { ComponentPick, Discipline } from "../../../core/api/types";
import { addDays, validateNewIssue } from "../logic";
import { firstImage, IMAGE_TYPES } from "../../../core/files";
import { addSnapshot, pickImage } from "./Snapshots";
import { Icon } from "../../../core/ui/icons";

/** A snapshot that failed after the issue was saved: the issue exists, so tell the user on the next page. */
const alertLater = (m: string) => setTimeout(() => toast(`Issue saved, but a snapshot was not: ${m}`, 6000), 300);

export function AddIssueDrawer({ code, components, disciplines, prefix, today, onClose, onCreated }: {
  code: string; components: ComponentPick[]; disciplines: Discipline[]; prefix: string; today: string;
  onClose: () => void; onCreated: (prefix: string, issueCode: string) => void;
}) {
  const blank = { component: prefix, discipline: "", status: "Open", target_date: addDays(today, 14) as string | null,
                  gridline: "", location: "", description: "", rfi_ref: "", drawing_ref: "" };
  const [v, setV] = useState(blank);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [badDate, setBadDate] = useState(false);
  const [pics, setPics] = useState<File[]>([]);
  const addPics = (list: ArrayLike<File> | null | undefined) => setPics((p) => [...p, ...Array.from(list ?? []).filter((f) => IMAGE_TYPES.includes(f.type))].slice(0, 10));
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const dirty = JSON.stringify(v) !== JSON.stringify(blank) || badDate || pics.length > 0;
  const close = () => (dirty ? setConfirm(true) : onClose());
  const saved = useRef(false);                       // once created, opening the new issue must not ask
  const release = useLeaveGuard(dirty && !saved.current);
  const next = components.find((c) => c.prefix === v.component)?.next_code;

  const save = async () => {
    if (badDate) return setError(`Target date: ${DATE_HINT_ROW}`);
    const problem = validateNewIssue(v);
    if (problem) return setError(problem);
    setBusy(true);
    try {
      const { component, ...fields } = v;
      const res = await rpc("issues_create", { code, prefix: component, ...fields, created_on: today });
      saved.current = true; release();
      if (pics.length) {
        try {
          const { project_id } = await rpc("issues_snapshots", { id: res.id });
          for (const f of pics) await addSnapshot({ issueId: res.id, projectId: project_id, file: f });
        } catch (e) { alertLater((e as Error).message); }
      }
      setBusy(false);
      onCreated(component, res.code);
    } catch (e) { setError((e as Error).message); setBusy(false); }
  };

  return (
    <>
      <Drawer title="New issue" onClose={close}
        sub={next ? <>Code will be assigned as <b className="code">{next}</b></> : "Pick a component to see the code"}
        footer={<>
          <InfoIcon tip={["Saving opens the issue page so you can add resolution entries.", "“Latest action by” comes from the newest resolution log entry.", "It shows “–” until one is added."]} />
          <button className="btn" onClick={close}>Cancel</button>
          <button className="btn primary" disabled={busy} onClick={save}>Save issue</button>
        </>}>
        {error && <ErrorBox message={error} />}
        <Field label="Component">
          <select value={v.component} onChange={set("component")} aria-label="Component">
            <option value="">Select a component…</option>
            {components.map((c) => <option key={c.prefix} value={c.prefix}>{c.name} ({c.prefix})</option>)}
          </select>
        </Field>
        <div className="grid3">
          <Field label="Discipline">
            <select value={v.discipline} onChange={set("discipline")} aria-label="Discipline">
              <option value="">Select…</option>
              {disciplines.map((d) => <option key={d.code} value={d.code}>{d.code}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={v.status} onChange={set("status")} aria-label="Status"><option>Open</option><option>Critical</option></select>
          </Field>
          <Field label="Target date"><DateInput ariaLabel="Target date" value={v.target_date} onChange={(d) => setV({ ...v, target_date: d })} onValidity={(ok) => setBadDate(!ok)} /></Field>
        </div>
        <div className="grid2">
          <Field label="Gridline">
            <div className="row gap6"><input value={v.gridline} onChange={set("gridline")} placeholder="Place a pin or type" aria-label="Gridline" />
              <button className="btn icon pin-btn" disabled title="Place the pin after saving: issue page › Gridline › 📍" aria-label="Place pin"><Icon name="place" /></button></div>
          </Field>
          <Field label="Location"><input value={v.location} onChange={set("location")} placeholder="e.g. Core wall, west" aria-label="Location" /></Field>
        </div>
        <Field label="Issue Description"><textarea rows={3} value={v.description} onChange={set("description")} placeholder="Describe the clash or defect" aria-label="Issue Description" /></Field>
        <div className="grid2">
          <Field label="RFI reference"><input value={v.rfi_ref} onChange={set("rfi_ref")} aria-label="RFI reference" /></Field>
          <Field label="Drawings reference"><input value={v.drawing_ref} onChange={set("drawing_ref")} aria-label="Drawings reference" /></Field>
        </div>
        <Field label="Snapshots">
          <div className="dropzone" role="button" tabIndex={0} data-testid="snap-drop" aria-label="Add snapshots"
            onClick={async () => { const f = await pickImage(); if (f) addPics([f]); }}
            onKeyDown={async (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); const f = await pickImage(); if (f) addPics([f]); } }}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addPics(e.dataTransfer.files); }}
            onPaste={(e) => { if (firstImage(e.clipboardData)) { e.preventDefault(); addPics(e.clipboardData.files); } }}>
            Drag images here or click to upload
          </div>
          {pics.length > 0 && <div className="pic-list">{pics.map((f, k) => (
            <span key={k} className="pending-snap"><span className="ellipsis">{f.name}</span><button type="button" className="x" aria-label={`Remove ${f.name}`} onClick={() => setPics(pics.filter((_, j) => j !== k))}><Icon name="close" /></button></span>))}</div>}
        </Field>
      </Drawer>
      {confirm && <ConfirmDiscard onKeep={() => setConfirm(false)} onDiscard={onClose} />}
    </>
  );
}
